import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  IconButton,
  CircularProgress,
  Avatar,
  Badge,
  Fade,
  Collapse,
} from "@mui/material";
import {
  CalendarToday,
  Event,
  OpenInNew,
  ExpandMore,
  ExpandLess,
  CalendarMonth,
} from "@mui/icons-material";
import ActionsService from "../../services/actions.service";
import { showMessage } from "../../customhooks/useNotifier";
import knowledgebaseAgentService from "../../services/knowledgebase-agent.service";

const UpcomingMeetings = () => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [hasCalendarConnection, setHasCalendarConnection] = useState(false);
  const [kbData, setKbData] = useState([]);

  /* 🔥 PAGINATION STATE */
  const [page, setPage] = useState(1);
  const pageSize = 5;

  useEffect(() => {
    const fetchMeetings = async () => {
      setLoading(true);

      try {
        const kbList = await knowledgebaseAgentService.getAllKnowledgeBases();
        if (!kbList?.length || !kbList[0]?.id) {
          setHasCalendarConnection(false);
          setMeetings([]);
          return;
        }

        setKbData(kbList);

        const formDataId = kbList[0].id;

        const conn = await ActionsService.getConnection(formDataId);
        if (!conn?.id) {
          setHasCalendarConnection(false);
          setMeetings([]);
          return;
        }

        setHasCalendarConnection(true);

        const evts = await ActionsService.listEvents({});

        const transformed = (evts || []).map((e) => ({
          id: e.id,
          summary: e.summary || "Untitled Event",
          description: e.description || "",
          start: {
            dateTime: e.start?.dateTime || e.start?.date,
            timeZone: e.start?.timeZone || "America/Los_Angeles",
          },
          end: {
            dateTime: e.end?.dateTime || e.end?.date,
            timeZone: e.end?.timeZone || "America/Los_Angeles",
          },
          attendees: e.attendees || [],
          htmlLink: e.htmlLink || null,
          status: e.status || "confirmed",
        }));

        setMeetings(transformed);
      } catch (error) {
        console.error("❌ Failed to fetch meetings:", error);
        setHasCalendarConnection(false);
        setMeetings([]);
        showMessage(
          "Failed to load meetings. Please try again later.",
          "warning"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchMeetings();
  }, []);

  const handleExpand = (id) => {
    const newExpanded = new Set(expandedItems);
    newExpanded.has(id) ? newExpanded.delete(id) : newExpanded.add(id);
    setExpandedItems(newExpanded);
  };

  const formatDateTime = (date) =>
    new Date(date).toLocaleString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

  const formatDate = (date) =>
    new Date(date).toLocaleString("en-US", { month: "short", day: "numeric" });

  const isToday = (date) => {
    const d = new Date(date);
    const t = new Date();
    return (
      d.getDate() === t.getDate() &&
      d.getMonth() === t.getMonth() &&
      d.getFullYear() === t.getFullYear()
    );
  };

  const isTomorrow = (date) => {
    const d = new Date(date);
    const t = new Date();
    t.setDate(t.getDate() + 1);
    return (
      d.getDate() === t.getDate() &&
      d.getMonth() === t.getMonth() &&
      d.getFullYear() === t.getFullYear()
    );
  };

  const getDateLabel = (dt) =>
    isToday(dt) ? "Today" : isTomorrow(dt) ? "Tomorrow" : formatDate(dt);

  const getInitials = (name) =>
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "?";

  const sortedMeetings = [...meetings].sort(
    (a, b) => new Date(a.start.dateTime) - new Date(b.start.dateTime)
  );

  /* 🔥 PAGINATION LOGIC */
  const totalMeetings = sortedMeetings.length;
  const totalPages = Math.ceil(totalMeetings / pageSize);

  const paginatedMeetings = sortedMeetings.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  /* ✨ LOADING UI */
  if (loading) {
    return (
      <Card sx={{ height: "100%", borderRadius: 4, boxShadow: 4 }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={48} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  /* CONNECT */
  const handleConnectCalendar = async () => {
    try {
      const { url } = await ActionsService.getAuthUrl(kbData[0].id, "action");
      window.location.href = url;
    } catch (err) {
      console.log("handleConnectCalendar err--->", err);
    }
  };

  /* NO CONNECTION */
  if (!hasCalendarConnection) {
    return (
      <Card sx={{ height: "100%", borderRadius: 4 }}>
        <Box
          sx={{
            background: "linear-gradient(135deg,#f59e0b,#d97706)",
            p: 3,
            color: "white",
            display: "flex",
          }}
        >
          <CalendarToday sx={{ fontSize: 28, mr: "1rem" }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Calendar Not Connected
          </Typography>
        </Box>

        <CardContent sx={{ textAlign: "center", py: 4 }}>
          <CalendarToday sx={{ fontSize: 64, opacity: 0.3 }} />
          <Typography variant="h6">Connect Google Calendar</Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Connect your calendar to see scheduled meetings.
          </Typography>

          <Button variant="contained" onClick={handleConnectCalendar}>
            Connect Calendar
          </Button>
        </CardContent>
      </Card>
    );
  }

  /* MAIN UI */
  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 4,
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
        border: "1px solid rgba(0, 0, 0, 0.05)",
        overflow: "hidden",
        background: "white",
      }}
    >
      {/* Header with gradient */}
      <Box
        sx={{
          background: "linear-gradient(135deg,#f59e0b,#d97706)",
          color: "white",
          p: 3,
          pb: 2.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: "rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backdropFilter: "blur(10px)",
                }}
              >
                <CalendarMonth sx={{ fontSize: 24 }} />
              </Box>

              <Typography
                variant="h5"
                sx={{ fontWeight: 700, fontSize: "1.5rem" }}
              >
                Upcoming Meetings
              </Typography>
            </Box>

            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.9)", ml: 7 }}
            >
              {sortedMeetings.length > 0
                ? `${sortedMeetings.length} meeting${
                    sortedMeetings.length > 1 ? "s" : ""
                  } scheduled 🎉`
                : "No meetings scheduled"}
            </Typography>
          </Box>
          {paginatedMeetings.some((m) => isToday(m.start.dateTime)) && (
            <Badge
              badgeContent={
                paginatedMeetings.filter((m) => isToday(m.start.dateTime))
                  .length
              }
              color="error"
            >
              <Event sx={{ fontSize: 24, color: "white" }} />
            </Badge>
          )}
        </Box>
      </Box>

      <CardContent sx={{ flex: 1, p: 0 }}>
        <List sx={{ flex: 1, overflow: "auto", p: 2, pb: 2 }}>
          {paginatedMeetings.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Event sx={{ fontSize: 64, opacity: 0.3 }} />
              <Typography>No upcoming meetings</Typography>
            </Box>
          ) : (
            <>
              {paginatedMeetings.map((meeting, i) => (
                <MeetingItem
                  key={meeting.id}
                  meeting={meeting}
                  isExpanded={expandedItems.has(meeting.id)}
                  onExpand={() => handleExpand(meeting.id)}
                  formatDateTime={formatDateTime}
                  getDateLabel={getDateLabel}
                  isToday={isToday}
                  getInitials={getInitials}
                  index={i}
                />
              ))}
            </>
          )}
        </List>

        {/* 🔥 PAGINATION BUTTONS */}
        {totalPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              pb: 2,
            }}
          >
            <Button
              variant="outlined"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>

            <Typography>
              Page {page} / {totalPages}
            </Typography>

            <Button
              variant="outlined"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

/* MEETING ITEM COMPONENT */
const MeetingItem = ({
  meeting,
  isExpanded,
  onExpand,
  formatDateTime,
  getDateLabel,
  isToday,
  getInitials,
  index,
}) => {
  const attendeeName =
    meeting.attendees?.[0]?.displayName ||
    meeting.attendees?.[0]?.email ||
    "Guest";

  return (
    <Fade in timeout={300 + index * 100}>
      <Box>
        {/* MAIN LIST ITEM */}
        <ListItem
          sx={{
            flexDirection: "column",
            borderRadius: 3,
            mb: 2,
            border: "1px solid rgba(0,0,0,0.1)",
            p: 2,
            backgroundColor: "background.paper",
          }}
        >
          <Box sx={{ display: "flex", width: "100%", gap: 2 }}>
            <Box sx={{ minWidth: 60, textAlign: "center" }}>
              <Typography variant="subtitle2">
                {formatDateTime(meeting.start.dateTime)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getDateLabel(meeting.start.dateTime)}
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {meeting.summary}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDateTime(meeting.start.dateTime)} →{" "}
                {formatDateTime(meeting.end.dateTime)}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                With: {attendeeName}
              </Typography>
            </Box>

            <Box>
              <IconButton size="small" onClick={onExpand}>
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>

              {meeting.htmlLink && (
                <IconButton
                  size="small"
                  href={meeting.htmlLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <OpenInNew fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* COLLAPSIBLE DETAILS */}
          <Collapse in={isExpanded} timeout={300} unmountOnExit>
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: "1px solid",
                borderColor: "divider",
                width: "100%",
              }}
            >
              {meeting.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2, whiteSpace: "pre-line" }}
                >
                  {meeting.description}
                </Typography>
              )}

              {meeting.attendees?.length > 0 && (
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Avatar sx={{ width: 36, height: 36 }}>
                    {getInitials(attendeeName)}
                  </Avatar>

                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {attendeeName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {meeting.attendees[0].email}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </ListItem>
      </Box>
    </Fade>
  );
};

export default UpcomingMeetings;
